import React from 'react'
import { Chart } from '../../legacy-design-system/charts/chart'
import * as Highcharts from 'highcharts'
import { I18nConsumer } from '../i18n-provider'
import { IntlShape } from 'react-intl'
import {
  buildChartSeriesOptions,
  getChartTooltipHeaderTpl,
  getChartTooltipSeriesRowTpl,
  MARKER_RADIUS,
  getChartPointBySeriesID,
  setArrowForSharedTooltip,
  getChartTooltipContainerCss,
  sharedTooltipPadding,
  linearGradient,
  ChartSeriesConfig,
  buildCrosshair,
  buildYAxisPlotLinesOptions
} from '../../legacy-design-system/charts/index'
import { theme } from '../../styled'
import { DateRangeOption } from '../../legacy-design-system/date-picker'
import {
  dateFormatConfig,
  xAxisMaxPadding,
  xAxisMinPadding,
  yAxisBaseConfig,
  bold
} from './index'

export type VulnSeverityType = 'NOTE' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL'

export type VulnSeverityFiltersType = 'ALL' | VulnSeverityType

export type VulnsSeverityBreakdown = Record<VulnSeverityType, number[]>

const TOP_SERIES_Z_INDEX = 5

const CHART_SERIES: ChartSeriesConfig<VulnSeverityType> = {
  NOTE: {
    zIndex: TOP_SERIES_Z_INDEX,
    id: 'NOTE',
    lineColor: theme.colors.gray050,
    areaColor: {
      linearGradient,
      stops: [
        [0, theme.secondaryColors.chartNoteAreaBottom],
        [1, theme.secondaryColors.chartNoteAreaTop]
      ]
    },
    areaColorBg: theme.secondaryColors.chartNoteAreaBg
  },
  LOW: {
    zIndex: TOP_SERIES_Z_INDEX,
    id: 'LOW',
    lineColor: theme.colors.gray040,
    areaColor: {
      linearGradient,
      stops: [
        [0, theme.secondaryColors.chartLowAreaBottom],
        [1, theme.secondaryColors.chartLowAreaTop]
      ]
    },
    areaColorBg: theme.secondaryColors.chartLowAreaBg
  },
  MEDIUM: {
    zIndex: TOP_SERIES_Z_INDEX,
    id: 'MEDIUM',
    lineColor: theme.colors.yellow,
    areaColor: {
      linearGradient,
      stops: [
        [0, theme.secondaryColors.chartMediumAreaBottom],
        [1, theme.secondaryColors.chartMediumAreaTop]
      ]
    },
    areaColorBg: theme.secondaryColors.chartMediumAreaBg
  },
  HIGH: {
    zIndex: TOP_SERIES_Z_INDEX,
    id: 'HIGH',
    lineColor: theme.colors.orange,
    areaColor: {
      linearGradient,
      stops: [
        [0, theme.secondaryColors.chartHighAreaBottom],
        [1, theme.secondaryColors.chartHighAreaTop]
      ]
    },
    areaColorBg: theme.secondaryColors.chartHighAreaBg
  },
  CRITICAL: {
    zIndex: TOP_SERIES_Z_INDEX,
    id: 'CRITICAL',
    lineColor: theme.colors.red,
    areaColor: {
      linearGradient,
      stops: [
        [0, theme.secondaryColors.chartCriticalAreaBottom],
        [1, theme.secondaryColors.chartCriticalAreaTop]
      ]
    },
    areaColorBg: theme.secondaryColors.chartCriticalAreaBg
  }
}

interface Props {
  /**
   * chart height
   */
  readonly height: number
  /**
   * values used in order to fill the Y axis data points and tooltips.
   */
  readonly vulnsBreakdown: VulnsSeverityBreakdown
  /**
   * values used in order to fill the X axis data points.
   */
  readonly timeline: number[]
  /**
   * initial filter selected which controls just the first chart visualization.
   */
  readonly initialFilter: VulnSeverityFiltersType

  readonly dateRangeOption?: DateRangeOption
  readonly dateRangeLabel?: string
}

interface State {
  readonly filter: VulnSeverityFiltersType
  readonly animationEnabled: boolean
}

export class VulnsSeverityChart extends React.Component<Props, State> {
  public constructor(props) {
    super(props)
    setArrowForSharedTooltip()

    const { initialFilter } = props
    this.state = {
      filter: initialFilter,
      animationEnabled: true
    }
  }

  public render() {
    return (
      <I18nConsumer>
        {({ intl }) => <Chart options={this.buildChartOptions(intl)} />}
      </I18nConsumer>
    )
  }

  private updateSelectedFilterState = (filter: VulnSeverityFiltersType) => {
    this.setState({
      filter,
      animationEnabled: false // chart series animation should only happen in the initial render
    })
  }

  private isAllFilterSelected = (): boolean => {
    const { filter } = this.state
    return filter === 'ALL'
  }

  private buildChartOptions = (intl: IntlShape): Highcharts.Options => {
    const { height } = this.props
    const { filter, animationEnabled } = this.state

    const isAllFilterSelected = this.isAllFilterSelected()
    const renderIndividualTooltip = this.renderIndividualTooltip
    const renderSharedTooltip = this.renderSharedTooltip

    return {
      chart: {
        height: height.valueOf(),
        type: 'areaspline',
        plotBackgroundColor: 'transparent', // this prop needs to be set in order to get chart.plotBackground defined
        events: {
          click: () =>
            isAllFilterSelected
              ? undefined
              : this.updateSelectedFilterState('ALL'),
          load() {
            // @ts-ignore plotBackground is available in 8.2 for this workaround, but not in the TS definition
            this.plotBackground.htmlCss({
              cursor: isAllFilterSelected ? undefined : 'pointer'
            })
          }
        }
      },
      title: {
        text: undefined
      },
      tooltip: {
        shared: isAllFilterSelected,
        backgroundColor: theme.colors.gray000,
        borderRadius: 8,
        padding: 0,
        borderWidth: 0,
        shadow: true,
        shape: 'callout',
        useHTML: true,

        formatter() {
          if (isAllFilterSelected) {
            return renderSharedTooltip(this.points, this.x, intl)
          } else {
            return this.series.options.id === filter
              ? renderIndividualTooltip(this.y, this.x, intl)
              : false
          }
        }
      },
      plotOptions: {
        series: {
          animation: animationEnabled,
          marker: {
            enabled: false,
            fillColor: theme.colors.white,
            symbol: 'circle',
            radius: MARKER_RADIUS
          },
          stacking: 'normal'
        }
      },
      xAxis: [
        this.getXAxisOptions(isAllFilterSelected),
        this.getDateRangeLabelXAxisOptions()
      ],
      yAxis: {
        ...yAxisBaseConfig,
        plotLines: isAllFilterSelected ? this.getPlotLines() : undefined
      },
      series: isAllFilterSelected
        ? this.buildAllFilterSeriesOptions()
        : this.buildFilterSeriesOptions()
    }
  }

  private getXAxisOptions = (
    showCrosshair: boolean
  ): Highcharts.XAxisOptions => {
    const { timeline, dateRangeOption } = this.props

    const formatterFn =
      !!dateRangeOption && dateFormatConfig[dateRangeOption] !== ''
        ? {
            formatter() {
              return Highcharts.dateFormat(
                dateFormatConfig[dateRangeOption],
                this.value
              ).toUpperCase()
            }
          }
        : undefined

    return {
      type: 'datetime',
      labels: {
        y: 45,
        align: 'center',
        style: {
          color: theme.colors.gray000,
          fontSize: '10'
        },
        ...formatterFn
      },
      crosshair: showCrosshair ? buildCrosshair(TOP_SERIES_Z_INDEX + 1) : false,

      lineColor: theme.colors.gray005,
      lineWidth: 0.5,
      tickLength: 0,

      maxPadding: xAxisMaxPadding,
      minPadding: xAxisMinPadding,

      tickPositions:
        !!dateRangeOption && dateFormatConfig[dateRangeOption] !== ''
          ? timeline
          : undefined
    }
  }

  private getDateRangeLabelXAxisOptions = (): Highcharts.XAxisOptions => {
    const { timeline, dateRangeLabel } = this.props

    return {
      linkedTo: 0,
      type: 'datetime',
      labels: {
        y: -20,
        align: 'center',
        style: {
          color: theme.colors.black,
          fontWeight: theme.fontWeights.primary.bold.toString(),
          fontFamily: theme.fontFamilies.primaryCondensed,
          fontSize: '13'
        },
        formatter() {
          return this.value === timeline[0] && !!dateRangeLabel
            ? dateRangeLabel
            : ''
        }
      },

      lineColor: theme.colors.transparent,
      lineWidth: 0,
      tickLength: 0,

      maxPadding: xAxisMaxPadding,
      minPadding: xAxisMinPadding,

      tickPositions: [timeline[0]]
    }
  }

  private getPlotLines = (): Highcharts.YAxisPlotLinesOptions[] => {
    const {
      vulnsBreakdown: { CRITICAL, HIGH, MEDIUM }
    } = this.props

    const maxPeak = Math.max(...CRITICAL, ...HIGH, ...MEDIUM)

    return !!maxPeak && maxPeak > 0
      ? [buildYAxisPlotLinesOptions(maxPeak, TOP_SERIES_Z_INDEX + 1)]
      : []
  }

  private buildAllFilterSeriesOptions =
    (): Highcharts.SeriesAreasplineOptions[] => {
      const { vulnsBreakdown, timeline } = this.props

      return [
        buildChartSeriesOptions(
          vulnsBreakdown.CRITICAL,
          timeline,
          CHART_SERIES.CRITICAL.id,
          CHART_SERIES.CRITICAL.lineColor,
          CHART_SERIES.CRITICAL.areaColor,
          CHART_SERIES.CRITICAL.zIndex,
          'pointer',
          true,
          true,
          () => this.updateSelectedFilterState('CRITICAL')
        ),
        buildChartSeriesOptions(
          vulnsBreakdown.HIGH,
          timeline,
          CHART_SERIES.HIGH.id,
          CHART_SERIES.HIGH.lineColor,
          CHART_SERIES.HIGH.areaColor,
          CHART_SERIES.HIGH.zIndex,
          'pointer',
          true,
          true,
          () => this.updateSelectedFilterState('HIGH')
        ),
        buildChartSeriesOptions(
          vulnsBreakdown.MEDIUM,
          timeline,
          CHART_SERIES.MEDIUM.id,
          CHART_SERIES.MEDIUM.lineColor,
          CHART_SERIES.MEDIUM.areaColor,
          CHART_SERIES.MEDIUM.zIndex,
          'pointer',
          true,
          true,
          () => this.updateSelectedFilterState('MEDIUM')
        ),
        buildChartSeriesOptions(
          vulnsBreakdown.LOW,
          timeline,
          CHART_SERIES.LOW.id,
          CHART_SERIES.LOW.lineColor,
          CHART_SERIES.LOW.areaColor,
          CHART_SERIES.LOW.zIndex,
          'pointer',
          true,
          true,
          () => this.updateSelectedFilterState('LOW')
        ),

        buildChartSeriesOptions(
          vulnsBreakdown.NOTE,
          timeline,
          CHART_SERIES.NOTE.id,
          CHART_SERIES.NOTE.lineColor,
          CHART_SERIES.NOTE.areaColor,
          CHART_SERIES.NOTE.zIndex,
          'pointer',
          true,
          true,
          () => this.updateSelectedFilterState('NOTE')
        )
      ]
    }

  private buildFilterSeriesOptions =
    (): Highcharts.SeriesAreasplineOptions[] => {
      const { timeline, vulnsBreakdown } = this.props
      const { filter } = this.state

      const isCriticalFilter = filter === 'CRITICAL'
      const isHighFilter = filter === 'HIGH'
      const isMediumFilter = filter === 'MEDIUM'
      const isLowFilter = filter === 'LOW'
      const isNoteFilter = filter === 'NOTE'

      const seriesOptions: Highcharts.SeriesAreasplineOptions[] = []

      seriesOptions.push(
        buildChartSeriesOptions(
          vulnsBreakdown.CRITICAL,
          timeline,
          CHART_SERIES.CRITICAL.id,
          CHART_SERIES.CRITICAL.lineColor,
          isCriticalFilter
            ? CHART_SERIES.CRITICAL.areaColor
            : CHART_SERIES.CRITICAL.areaColorBg,
          CHART_SERIES.CRITICAL.zIndex,
          isCriticalFilter ? undefined : 'pointer',
          isCriticalFilter,
          true,
          isCriticalFilter
            ? undefined
            : () => this.updateSelectedFilterState('ALL')
        )
      )

      seriesOptions.push(
        buildChartSeriesOptions(
          vulnsBreakdown.HIGH,
          timeline,
          CHART_SERIES.HIGH.id,
          CHART_SERIES.HIGH.lineColor,
          isHighFilter
            ? CHART_SERIES.HIGH.areaColor
            : CHART_SERIES.HIGH.areaColorBg,
          CHART_SERIES.HIGH.zIndex,
          isHighFilter ? undefined : 'pointer',
          isHighFilter,
          true,
          isHighFilter ? undefined : () => this.updateSelectedFilterState('ALL')
        )
      )

      seriesOptions.push(
        buildChartSeriesOptions(
          vulnsBreakdown.MEDIUM,
          timeline,
          CHART_SERIES.MEDIUM.id,
          CHART_SERIES.MEDIUM.lineColor,
          isMediumFilter
            ? CHART_SERIES.MEDIUM.areaColor
            : CHART_SERIES.MEDIUM.areaColorBg,
          CHART_SERIES.MEDIUM.zIndex,
          isMediumFilter ? undefined : 'pointer',
          isMediumFilter,
          true,
          isMediumFilter
            ? undefined
            : () => this.updateSelectedFilterState('ALL')
        )
      )

      seriesOptions.push(
        buildChartSeriesOptions(
          vulnsBreakdown.LOW,
          timeline,
          CHART_SERIES.LOW.id,
          CHART_SERIES.LOW.lineColor,
          isLowFilter
            ? CHART_SERIES.LOW.areaColor
            : CHART_SERIES.LOW.areaColorBg,
          CHART_SERIES.LOW.zIndex,
          isLowFilter ? undefined : 'pointer',
          isLowFilter,
          true,
          isLowFilter ? undefined : () => this.updateSelectedFilterState('ALL')
        )
      )

      seriesOptions.push(
        buildChartSeriesOptions(
          vulnsBreakdown.NOTE,
          timeline,
          CHART_SERIES.NOTE.id,
          CHART_SERIES.NOTE.lineColor,
          isNoteFilter
            ? CHART_SERIES.NOTE.areaColor
            : CHART_SERIES.NOTE.areaColorBg,
          CHART_SERIES.NOTE.zIndex,
          isNoteFilter ? undefined : 'pointer',
          isNoteFilter,
          true,
          isNoteFilter ? undefined : () => this.updateSelectedFilterState('ALL')
        )
      )

      return seriesOptions
    }

  private renderSharedTooltip = (
    points,
    timestamp: number,
    intl: IntlShape
  ) => {
    const seriesPoints: Record<VulnSeverityType, any> = {
      CRITICAL: getChartPointBySeriesID(CHART_SERIES.CRITICAL.id, points),
      HIGH: getChartPointBySeriesID(CHART_SERIES.HIGH.id, points),
      MEDIUM: getChartPointBySeriesID(CHART_SERIES.MEDIUM.id, points),
      LOW: getChartPointBySeriesID(CHART_SERIES.LOW.id, points),
      NOTE: getChartPointBySeriesID(CHART_SERIES.NOTE.id, points)
    }

    const totalSeriesCount =
      seriesPoints.CRITICAL.y +
      seriesPoints.HIGH.y +
      seriesPoints.MEDIUM.y +
      seriesPoints.LOW.y +
      seriesPoints.NOTE.y

    let tooltipTpl = getChartTooltipHeaderTpl(
      intl.formatMessage(
        {
          id: 'pluralCount_traces_vulnerabilitiesGridCount'
        },
        {
          count: totalSeriesCount
        }
      ),
      Highcharts.dateFormat('%b %e, %Y %H:%M', timestamp)
    )

    tooltipTpl += '<div style="margin-bottom: 16px"></div>'

    const seriesPercentages: Record<VulnSeverityType, number> = {
      CRITICAL: getPercentage(totalSeriesCount, seriesPoints.CRITICAL.y),
      HIGH: getPercentage(totalSeriesCount, seriesPoints.HIGH.y),
      MEDIUM: getPercentage(totalSeriesCount, seriesPoints.MEDIUM.y),
      LOW: getPercentage(totalSeriesCount, seriesPoints.LOW.y),
      NOTE: getPercentage(totalSeriesCount, seriesPoints.NOTE.y)
    }

    const totalSeriesPercentage =
      seriesPercentages.CRITICAL +
      seriesPercentages.HIGH +
      seriesPercentages.MEDIUM +
      seriesPercentages.LOW +
      seriesPercentages.NOTE

    if (totalSeriesPercentage !== 100) {
      const percentages = [
        seriesPercentages.CRITICAL,
        seriesPercentages.HIGH,
        seriesPercentages.MEDIUM,
        seriesPercentages.LOW,
        seriesPercentages.NOTE
      ]

      if (totalSeriesPercentage === 101) {
        // eslint-disable-next-line prefer-spread
        switch (Math.max.apply(Math, percentages)) {
          case seriesPercentages.NOTE:
            seriesPercentages.NOTE -= 1
            break
          case seriesPercentages.LOW:
            seriesPercentages.LOW -= 1
            break
          case seriesPercentages.MEDIUM:
            seriesPercentages.MEDIUM -= 1
            break
          case seriesPercentages.HIGH:
            seriesPercentages.HIGH -= 1
            break
          case seriesPercentages.CRITICAL:
            seriesPercentages.CRITICAL -= 1
            break
        }
      } else if (totalSeriesPercentage === 99) {
        // eslint-disable-next-line prefer-spread
        switch (Math.min.apply(Math, percentages)) {
          case seriesPercentages.NOTE:
            seriesPercentages.NOTE += 1
            break
          case seriesPercentages.LOW:
            seriesPercentages.LOW += 1
            break
          case seriesPercentages.MEDIUM:
            seriesPercentages.MEDIUM += 1
            break
          case seriesPercentages.HIGH:
            seriesPercentages.HIGH += 1
            break
          case seriesPercentages.CRITICAL:
            seriesPercentages.CRITICAL += 1
            break
        }
      }
    }

    const tooltipSeriesRowTplConfig: Array<{
      readonly lineColor: string
      readonly i18nMessageID: string
      readonly count: number
      readonly percentage: number
    }> = [
      {
        lineColor: seriesPoints.CRITICAL.series.options.lineColor,
        i18nMessageID: 'detail_vulnerabilities_nVulnsTypeCritical',
        count: seriesPoints.CRITICAL.y,
        percentage: seriesPercentages.CRITICAL
      },
      {
        lineColor: seriesPoints.HIGH.series.options.lineColor,
        i18nMessageID: 'detail_vulnerabilities_nVulnsTypeHigh',
        count: seriesPoints.HIGH.y,
        percentage: seriesPercentages.HIGH
      },
      {
        lineColor: seriesPoints.MEDIUM.series.options.lineColor,
        i18nMessageID: 'detail_vulnerabilities_nVulnsTypeMedium',
        count: seriesPoints.MEDIUM.y,
        percentage: seriesPercentages.MEDIUM
      },
      {
        lineColor: seriesPoints.LOW.series.options.lineColor,
        i18nMessageID: 'detail_vulnerabilities_nVulnsTypeLow',
        count: seriesPoints.LOW.y,
        percentage: seriesPercentages.LOW
      },
      {
        lineColor: seriesPoints.NOTE.series.options.lineColor,
        i18nMessageID: 'detail_vulnerabilities_nVulnsTypeNote',
        count: seriesPoints.NOTE.y,
        percentage: seriesPercentages.NOTE
      }
    ]

    tooltipSeriesRowTplConfig.forEach(
      ({ lineColor, i18nMessageID, count, percentage }) => {
        tooltipTpl += getChartTooltipSeriesRowTpl(
          lineColor,
          intl.formatMessage(
            {
              id: i18nMessageID
            },
            {
              count,
              b: bold
            }
          ),
          percentage
        )
      }
    )

    return (
      '<div style="' +
      getChartTooltipContainerCss(sharedTooltipPadding) +
      '" >' +
      tooltipTpl +
      '</div>'
    )
  }

  private renderIndividualTooltip = (
    value,
    timestamp: number,
    intl: IntlShape
  ) => {
    const { filter } = this.state

    let label

    switch (filter) {
      case 'NOTE':
        label = intl.formatMessage(
          {
            id: 'detail_vulnerabilities_nVulnsTypeNote'
          },
          {
            count: value,
            b: bold
          }
        )
        break
      case 'LOW':
        label = intl.formatMessage(
          {
            id: 'detail_vulnerabilities_nVulnsTypeLow'
          },
          {
            count: value,
            b: bold
          }
        )
        break
      case 'MEDIUM':
        label = intl.formatMessage(
          {
            id: 'detail_vulnerabilities_nVulnsTypeMedium'
          },
          {
            count: value,
            b: bold
          }
        )
        break
      case 'HIGH':
        label = intl.formatMessage(
          {
            id: 'detail_vulnerabilities_nVulnsTypeHigh'
          },
          {
            count: value,
            b: bold
          }
        )
        break
      case 'CRITICAL':
        label = intl.formatMessage(
          {
            id: 'detail_vulnerabilities_nVulnsTypeCritical'
          },
          {
            count: value,
            b: bold
          }
        )
        break
      default:
        label = ''
    }

    const tooltipTpl = getChartTooltipHeaderTpl(
      label,
      Highcharts.dateFormat('%b %e, %Y %H:%M', timestamp)
    )

    return (
      '<div style="' +
      getChartTooltipContainerCss(undefined) +
      '">' +
      tooltipTpl +
      '</div>'
    )
  }
}

const getPercentage = (total: number, value: number) => {
  return Math.round((value * 100) / total)
}
